import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { msalConfig, apiScopes } from './config/authConfig';
import { UserProvider } from './contexts/UserContext';
import { EditInitiativeProvider } from './contexts/EditInitiativeContext';
import axios from 'axios';

// Resolve the backend base URL, and ping it up front so we can block
// rendering the app entirely when the backend is unreachable.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

type ConnectivityFailureReason = 'backend' | 'database' | null;

// Cold starts on Azure can take well over 8s (startup.sh reinstalls deps on restart),
// so retry a few times before declaring the backend unreachable.
// Uses /health (not just /ping) so a DB outage is also caught, not just backend reachability.
const checkBackendConnectivity = async (): Promise<ConnectivityFailureReason> => {
  const attempts = 4;
  let lastReason: ConnectivityFailureReason = 'backend';
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const res = await axios.get(`${API_BASE_URL}/health`, { timeout: 15000 });
      if (res.data?.database === 'connected') {
        return null;
      }
      lastReason = 'database';
      console.error(`[STARTUP] Backend reachable but DB not connected (attempt ${attempt}/${attempts})`, res.data);
    } catch (err: any) {
      lastReason = 'backend';
      console.error(`[STARTUP] Backend not reachable at ${API_BASE_URL}/health (attempt ${attempt}/${attempts})`, err?.message || err);
    }
    if (attempt < attempts) {
      await new Promise((resolve) => setTimeout(resolve, attempt * 3000));
    }
  }
  return lastReason;
};

// Simple full-page notice shown instead of the app when the backend/DB can't be reached.
const ConnectivityError: React.FC<{ reason: ConnectivityFailureReason }> = ({ reason }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', background: '#f4f5f7', fontFamily: 'sans-serif',
  }}>
    <div style={{
      background: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
      padding: '40px 48px', maxWidth: '420px', textAlign: 'center',
    }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
      <h1 style={{ color: '#c0392b', margin: '0 0 12px', fontSize: '22px' }}>
        {reason === 'database' ? 'Database Connectivity Issue' : 'Backend Connectivity Issue'}
      </h1>
      <p style={{ color: '#444', margin: '0 0 24px', lineHeight: 1.5 }}>
        {reason === 'database'
          ? 'The app reached the backend, but the database connection failed.'
          : 'The app is unable to reach the backend.'}
        {' '}Please try refreshing, and if the issue persists,
        contact the{' '}
        <a href="mailto:ganesh.narayanan-c-m@in.abb.com" style={{ color: '#c0392b' }}>app admin</a>.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#c0392b', color: '#fff', border: 'none', borderRadius: '6px',
          padding: '10px 24px', fontSize: '15px', cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  </div>
);

const backendReachablePromise = checkBackendConnectivity();

// Initialize MSAL instance
console.log('%c[MSAL] Creating PublicClientApplication instance', 'color: green; font-weight: bold');
const msalInstance = new PublicClientApplication(msalConfig);
console.log('[MSAL] Instance created successfully');

// Function to setup axios interceptor with MSAL token
const setupAxiosInterceptor = async () => {
  console.log('%c[Axios] Setting up interceptors', 'color: teal; font-weight: bold');

  // Guards against firing acquireTokenRedirect from multiple concurrent
  // requests at once - MSAL throws "interaction_in_progress" if a second
  // interactive call starts before the first one has navigated away.
  let redirectInProgress = false;

  axios.interceptors.request.use(
    async (config) => {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        return Promise.reject(new Error('No signed-in MSAL account - request aborted instead of sending unauthenticated'));
      }
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: apiScopes.scopes,
          account: accounts[0],
        });
        config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
        return config;
      } catch (error) {
        // Any silent-acquisition failure (InteractionRequiredAuthError, or a
        // BrowserAuthError like monitor_window_timeout from a blocked/failed
        // hidden-iframe silent renewal) needs the same interactive fallback -
        // otherwise the request just fails forever with no redirect.
        if (!redirectInProgress) {
          redirectInProgress = true;
          msalInstance.acquireTokenRedirect({ scopes: apiScopes.scopes, account: accounts[0] });
        }
        return Promise.reject(error);
      }
    },
    (error) => {
      console.error('[Axios] Request error:', error);
      return Promise.reject(error);
    }
  );

  // Log 401 responses for diagnostics. Re-auth (if actually needed) is
  // already triggered from the request interceptor above when silent token
  // acquisition fails - a 401 here doesn't necessarily mean the session is
  // invalid (e.g. transient backend validation hiccups), so it's not treated
  // as a hard session-expired signal.
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.error('[Axios] ✗ Unauthorized (401) - token expired or invalid:', error.config?.url);
      }
      return Promise.reject(error);
    }
  );
  
  console.log('[Axios] Interceptors configured successfully');
};

// Renders the app, or a connectivity error screen if the backend/DB is unreachable.
const renderApp = (failureReason: ConnectivityFailureReason) => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );

  if (failureReason) {
    root.render(<ConnectivityError reason={failureReason} />);
    return;
  }

  // Do not send credentials by default to avoid CORS credential issues.
  axios.defaults.withCredentials = false;
  setupAxiosInterceptor();

  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <MsalProvider instance={msalInstance}>
          <UserProvider>
            <EditInitiativeProvider>
              <App />
            </EditInitiativeProvider>
          </UserProvider>
        </MsalProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
};

// Handle redirect promise
console.log('%c[MSAL] Initializing MSAL instance', 'color: purple; font-weight: bold');
msalInstance.initialize().then(() => {
  console.log('[MSAL] Instance initialized successfully');
  
  // Handle redirect responses (for redirect flow) - MUST be called first
  console.log('[MSAL] Handling redirect promise (must complete before rendering)...');
  msalInstance.handleRedirectPromise().then((response) => {
    if (response) {
      console.log('%c[MSAL] ✓ Redirect response received!', 'color: green; font-weight: bold', response);
      console.log('[MSAL] Account:', response.account?.username);
      msalInstance.setActiveAccount(response.account);
    } else {
      console.log('[MSAL] No redirect response (first load or not returning from redirect)');
    }
    
    // After redirect promise is handled, proceed with setup
    console.log('[MSAL] Redirect promise resolved, checking for existing accounts...');
    const accounts = msalInstance.getAllAccounts();
    console.log('[MSAL] Found', accounts.length, 'account(s)');
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      console.log('[MSAL] Setting first account as active:', accounts[0].username);
      msalInstance.setActiveAccount(accounts[0]);
    }
    
    // Add event callback for login success
    msalInstance.addEventCallback((event) => {
      console.log('[MSAL] Event callback triggered:', event.eventType);
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as any;
        const account = payload.account;
        console.log('%c[MSAL] ✓ LOGIN_SUCCESS event received', 'color: green; font-weight: bold');
        console.log('[MSAL] Setting active account:', account?.username);
        msalInstance.setActiveAccount(account);
      }
    });

    // Now render the app once we know whether the backend is reachable.
    backendReachablePromise.then(renderApp);
  }).catch((error) => {
    console.error('%c[MSAL] ✗ Redirect error:', 'color: red; font-weight: bold', error);
    // Even on error, proceed with rendering the app (if backend is reachable)
    backendReachablePromise.then(renderApp);
  });
});
